import { IBookingSlotsFilterBody, IFrontDeskDoctorRotaBody } from "@/api_framework/api_modals/Bookings";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useFrontDeskDoctorRotaAPI } from "@/app/hooks/api_hooks/bookings/useFrontDeskDoctorRotaAPI";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { buildBookingServiceMaps } from "@/app/utils/bookingServiceSelection";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useEffect, useMemo } from "react";
import FrontDeskOverbookingRow from "./FrontDeskOverbookingRow";
import { FRONT_DESK_OB_LOG_SOURCE } from "./helpers";

/* Same layout as OverBookingSlotsComponent, rows come from doctor ROTA for the searched filters */
const FrontDeskOverbookingComponent = () => {
  /* Instantiations Starts */
  const { NO_PWL_RESULTS } = useImageConstants();
  const { getRouteKey } = useRouter();
  const { filters, clinics } = useRouteInfo(getRouteKey("SLOTS", "id")).routeState.state as ISlotsState;
  const { customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const { execute: logClick } = useClickLogAPI();
  const { doctorRota, isLoading, execute: fetchDoctorRota } = useFrontDeskDoctorRotaAPI();
  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  /* Instantiations Ends */

  /* Constants Starts */
  const bookingFilters = filters as IBookingSlotsFilterBody | null;
  const clinicIds = bookingFilters?.clinic_ids ?? [];
  const rawCityId = bookingFilters?.city_id;
  const cityIds = (Array.isArray(rawCityId) ? rawCityId : [rawCityId]).filter(Boolean) as string[];
  const serviceTypeId = bookingFilters?.service_type_id;
  const subServiceId = bookingFilters?.sub_service_id;
  const bookingDate = bookingFilters?.booking_date;
  const canSearch = Boolean(subServiceId && bookingDate && (cityIds.length > 0 || clinicIds.length > 0));
  const rows = canSearch ? doctorRota : [];

  const { serviceTypeNamesByIdMap, subServiceByIdMap } = useMemo(
    () => buildBookingServiceMaps(serviceTypes ?? []),
    [serviceTypes],
  );
  const subService = subServiceByIdMap[subServiceId];
  /* Constants Ends */

  /* Use Effect Starts */
  useEffect(() => {
    fetchOrgServicesByFilter({ filterType: "booking_vms" });
  }, []);

  // Search / date change updates filters -> fetch providers having ROTA for clinic x sub service x date
  useEffect(() => {
    if (!canSearch) return;
    const body: IFrontDeskDoctorRotaBody = {
      ...(cityIds.length > 0 ? { city_id: cityIds } : {}),
      clinic_ids: clinicIds,
      sub_service_id: subServiceId,
      booking_date: bookingDate,
    };
    fetchDoctorRota(body).then((rotaList) => {
      logClick({
        event_type: "FRONT_DESK_OB_SEARCH",
        source: FRONT_DESK_OB_LOG_SOURCE,
        scr_id: "BOOKING_PAGE",
        client_id: customerId,
        pet_id: petId,
        vms_user_id: vmsUserId,
        filter_values: { ...body, service_type_id: serviceTypeId ?? null },
        items_count: rotaList?.length ?? 0,
        timestamp: Date.now(),
      });
    });
  }, [canSearch, bookingDate, subServiceId, cityIds.join(","), clinicIds.join(",")]);
  /* Use Effect Ends */

  return (
    <div className="px-4">
      <div className="">
        {!isLoading ? (
          rows.length > 0 ? (
            rows.map((rota, idx) => (
              <FrontDeskOverbookingRow
                // date / sub service in key so a new search resets the selected time
                key={`${bookingDate}:${subServiceId}:${idx}:${rota?.staff_id ?? rota?.provider_name}`}
                hideHeader={idx > 0}
                rota={rota}
                // API gives clinic name only; single selected clinic is the fallback
                clinicId={
                  clinics?.find((clinic) => clinic?.display_name === rota?.clinic_name)?.id ??
                  (clinicIds.length === 1 ? clinicIds[0] : undefined)
                }
                duration={Number(rota?.slot_duration) || Number(subService?.duration) || 0}
                date={bookingDate}
                service={{ id: serviceTypeId, title: serviceTypeNamesByIdMap[serviceTypeId] ?? "" }}
                subService={{ id: subServiceId, title: subService?.sub_service_name ?? "" }}
              />
            ))
          ) : (
            <NoDataFoundComponent
              imageURL={NO_PWL_RESULTS}
              message={canSearch ? "No Providers Found" : "Search Providers"}
              description={
                canSearch
                  ? "No provider has ROTA on the selected date for this sub service and clinic."
                  : "Select service, sub service, city and clinic, then search."
              }
            />
          )
        ) : (
          <>
            <SkeletonType1 className="mt-2" />
            <SkeletonType1 className="mt-2" />
          </>
        )}
      </div>
    </div>
  );
};

export default FrontDeskOverbookingComponent;
